/**
 * The books screens' client for `/api/books` (contracts/books-api.md).
 *
 * Bookkeeping is back-office work and says so when the connection is missing:
 * unlike a sale, nothing here queues offline. The one exception is Cash out at
 * the till, which goes through the core's own offline path instead of this
 * client.
 *
 * Every amount crossing this boundary is integer centavos.
 */

export type PlaceId = "drawer" | "cash_held" | "ewallet" | "capital_fund" | "treasurer";

export interface Place {
  id: PlaceId;
  name: string;
  balance: number;
  uncountedShifts: number;
  lastCount: { at: string; difference: number } | null;
}

export interface Position {
  places: Place[];
  total: number;
  unknownTenders: string[];
  waitingToCategorise: { count: number; amount: number };
  asOf: string;
}

export interface Category {
  id: string;
  name: string;
  kind: "setup" | "goods" | "running" | "deposit";
}

export interface ExpensePart {
  place: PlaceId | null;
  partnerId: string | null;
  amount: number;
}

export interface Expense {
  id: string;
  spentOn: string;
  dateText: string | null;
  supplier: string | null;
  description: string;
  categoryId: string;
  amount: number;
  isEstimate: boolean;
  reference: string | null;
  note: string | null;
  correctsId: string | null;
  reason: string | null;
  cashMovementId: string | null;
  sourceRow: number | null;
  createdAt: string;
  parts: ExpensePart[];
}

export interface CashOut {
  id: string;
  amount: number;
  reason: string;
  staffId: string;
  staffName: string | null;
  shiftId: string;
  createdAt: string;
  expenseId: string | null;
}

export interface Totals {
  from: string;
  to: string;
  byCategory: Array<{ categoryId: string; name: string; kind: string; amount: number }>;
  byKind: Record<string, number>;
  total: number;
}

export interface NewExpense {
  opId: string;
  spentOn: string;
  supplier?: string | null;
  description: string;
  categoryId: string;
  amount: number;
  parts: Array<{ place?: PlaceId; partner?: string; amount: number }>;
  isEstimate?: boolean;
  reference?: string | null;
  note?: string | null;
  cashMovementId?: string | null;
  /** The stock movements this purchase also wrote, sharing its `opId` (FR-129). */
  inventoryOpId?: string | null;
}

export interface Partner {
  id: string;
  name: string;
  role: string | null;
  subscribed: number;
  ownershipBp: number;
  cash: number;
  inKind: number;
  /** Paid in kind past the subscription — not capital; carried by an advance. */
  inKindBeyond: number;
  paid: number;
  owed: number;
  /** What the café owes this partner as a loan. */
  advanceOwed: number;
}

export interface CallShare {
  partnerId: string;
  amount: number;
  paid: number;
  outstanding: number;
  settled: boolean;
}

export interface CapitalCall {
  id: string;
  title: string;
  dueOn: string | null;
  total: number;
  paid: number;
  outstanding: number;
  shares: CallShare[];
}

export interface CapitalTable {
  partners: Partner[];
  totals: {
    subscribed: number;
    cash: number;
    inKind: number;
    inKindBeyond: number;
    paid: number;
    owed: number;
    advanceOwed: number;
  };
  calls: CapitalCall[];
}

export interface ProfitBody {
  period: { from: string; to: string };
  sales: number;
  costOfGoods: {
    amount: number;
    costedSales: number;
    uncostedSales: number;
    available: boolean;
    unpricedIngredients: Array<{ id: string; name: string }>;
  };
  runningCosts: { actual: number; accrued: number; total: number; estimated: boolean };
  operatingProfit: number;
  breakEven: {
    perMonth: number | null;
    perTradingDay: number | null;
    actualPerTradingDay: number | null;
  };
  setupSpend: number;
  payback: { months: number | null; basis?: string; reason?: string };
  tradingDays: number;
  /** Items an extension gave away (a loyalty reward), by module, at full price. */
  grantedFree?: Array<{ module: string; count: number; amount: number }>;
}

export type LineStatus = "open" | "closed" | "unpriced";

export interface PlanLine {
  id: string;
  name: string;
  estimate: number | null;
  status: LineStatus;
  note: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  spent: number;
  remaining: number;
  overrun: number;
}

export interface PlanCommitment {
  id: string;
  name: string;
  categoryId: string;
  monthlyAmount: number;
  isEstimate: boolean;
  startsOn: string;
  endsOn: string | null;
}

export interface PlanBody {
  lines: PlanLine[];
  openRemaining: number;
  unpricedLines: number;
  totalsAreAFloor: boolean;
  setupSpend: number;
  projectedTotal: number;
  moneyAvailable: number;
  capitalStillNeeded: number;
  commitments: PlanCommitment[];
  monthlyCommitted: number;
}

export interface SummaryBody {
  asOf: string;
  spent: {
    byCategory: Array<{ categoryId: string; name: string; kind: string; amount: number }>;
    byKind: Record<string, number>;
    total: number;
  };
  position: {
    places: Array<{ placeId: PlaceId; balance: number; uncountedShifts: number }>;
    total: number;
    uncountedShifts: number;
    unknownTenders: string[];
  };
  capital: CapitalTable;
  plan: PlanBody;
}

export class BooksRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
  ) {
    super(message);
    this.name = "BooksRequestError";
  }
}

export function booksApi(token: string) {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`/api/books${path}`, {
        method,
        headers: {
          authorization: `Bearer ${token}`,
          ...(body === undefined ? {} : { "content-type": "application/json" }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw new BooksRequestError(
        "No connection. The books need the internet — try again once it is back.",
        0,
        null,
      );
    }

    if (!response.ok) {
      let message = `That did not go through (${response.status}).`;
      let code: string | null = null;
      try {
        const parsed = (await response.json()) as { error?: { message?: string; code?: string } };
        message = parsed.error?.message ?? message;
        code = parsed.error?.code ?? null;
      } catch {
        // Not JSON — keep the generic sentence.
      }
      throw new BooksRequestError(message, response.status, code);
    }

    return (await response.json()) as T;
  }

  const dates = (from: string, to: string) => `from=${from}&to=${to}`;

  return {
    position: () => request<Position>("GET", "/position"),
    categories: () => request<{ categories: Category[] }>("GET", "/categories"),

    expenses: (from: string, to: string, offset = 0) =>
      request<{ expenses: Expense[]; nextOffset: number | null }>(
        "GET",
        `/expenses?${dates(from, to)}&limit=25&offset=${offset}`,
      ),
    totals: (from: string, to: string) => request<Totals>("GET", `/expenses/totals?${dates(from, to)}`),
    createExpense: (body: NewExpense) =>
      request<{ duplicate: boolean; expense: Expense }>("POST", "/expenses", body),
    correctExpense: (id: string, body: { opId: string; reason: string; replacement: Omit<NewExpense, "opId"> | null }) =>
      request<{ duplicate: boolean }>("POST", `/expenses/${id}/corrections`, body),

    cashOuts: () => request<{ cashOuts: CashOut[] }>("GET", "/cash-outs?status=waiting"),

    profit: (from: string, to: string) => request<ProfitBody>("GET", `/profit?${dates(from, to)}`),

    summary: () => request<SummaryBody>("GET", "/summary"),
    /**
     * The API needs the session's token, so a file cannot be fetched by a plain
     * link: it comes back as a blob the page hands to the browser itself.
     */
    exportCsv: async (path: string): Promise<Blob> => {
      const response = await fetch(`/api/books${path}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new BooksRequestError(`Could not download that (${response.status}).`, response.status, null);
      }
      return response.blob();
    },

    plan: () => request<PlanBody>("GET", "/plan"),
    addBudgetLine: (body: {
      name: string;
      estimate: number | null;
      status: LineStatus;
      note: string | null;
    }) => request<{ line: { id: string } }>("POST", "/budget-lines", body),
    setLineStatus: (id: string, body: { status: LineStatus; note: string | null }) =>
      request<{ id: string; status: LineStatus }>("POST", `/budget-lines/${id}/status`, body),
    addCommitment: (body: {
      name: string;
      categoryId: string;
      monthlyAmount: number;
      isEstimate: boolean;
      startsOn: string;
      replaces: string | null;
    }) => request<{ commitment: { id: string } }>("POST", "/commitments", body),

    partners: () => request<CapitalTable>("GET", "/partners"),
    /**
     * A contribution past the partner's subscription comes back as
     * `BEYOND_SUBSCRIPTION` and writes nothing; sending it again with
     * `beyondSubscription` — and the same `opId` — is the owner's confirmation.
     */
    contribute: (body: {
      opId: string;
      partnerId: string;
      amount: number;
      place: PlaceId;
      contributedOn: string;
      callId: string | null;
      beyondSubscription: boolean;
    }) => request<{ duplicate: boolean }>("POST", "/contributions", body),
    capitalCall: (body: { opId: string; title: string; dueOn: string | null; total: number; shares: null }) =>
      request<{ duplicate: boolean }>("POST", "/capital-calls", body),

    transfer: (body: { opId: string; from: PlaceId; to: PlaceId; amount: number; reason: string }) =>
      request<{ duplicate: boolean }>("POST", "/transfers", body),
    count: (body: { opId: string; place: PlaceId; counted: number; reason: string }) =>
      request<{ duplicate: boolean; difference: number }>("POST", "/counts", body),
  };
}

export type BooksApi = ReturnType<typeof booksApi>;
